import React, { useMemo, useState } from 'react';
import { Search, SparkIcon, Text } from '@spark';
import { formatDateTime } from '@/shared/utils/helpers';
import {
  activityLogIconSlug,
  filterActivityLogEntries,
} from '@/features/trainings/utils/activityLogUtils';
import { stripCloneSuffixInText } from '@/features/datasets/utils/datasetCloneNames';

function TimelineItem({ entry, isLast }) {
  const displayName = entry?.display_name || 'Activity';
  const description = stripCloneSuffixInText(entry?.description || '') || '';
  const createdDatetime = entry?.created_datetime || null;
  const iconSlug = activityLogIconSlug(displayName);

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 'var(--dimension-spacing-16)',
        width: '100%',
      }}
    >
      <Text
        variant="base-regular"
        color="secondary"
        as="div"
        style={{
          flex: '0 0 210px',
          paddingTop: 13,
          whiteSpace: 'nowrap',
        }}
      >
        {createdDatetime ? formatDateTime(createdDatetime) : '—'}
      </Text>
      <div
        style={{
          position: 'relative',
          flex: '0 0 48px',
          width: 48,
          alignSelf: 'stretch',
        }}
      >
        <div
          style={{
            width: 48,
            height: 48,
            borderRadius: 'var(--dimension-border-radius-08)',
            background: 'var(--semantic-button-secondary-bg-hover)',
            color: 'var(--semantic-button-secondary-content-on-surface)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
            zIndex: 1,
          }}
          aria-hidden="true"
        >
          <SparkIcon slug={iconSlug} size="medium" />
        </div>
        {!isLast ? (
          <div
            aria-hidden="true"
            style={{
              position: 'absolute',
              top: 48,
              bottom: 0,
              left: '50%',
              width: 1,
              marginBottom: 'calc(-1 * var(--dimension-spacing-24))',
              background: 'var(--semantic-border-default)',
              transform: 'translateX(-50%)',
            }}
          />
        ) : null}
      </div>
      <div style={{ flex: 1, minWidth: 0, paddingTop: 2 }}>
        <Text variant="base-semibold" color="primary" as="div">
          {displayName}
        </Text>
        {description ? (
          <Text
            variant="base-medium"
            color="secondary"
            as="div"
            style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
          >
            {description}
          </Text>
        ) : null}
      </div>
    </div>
  );
}

/**
 * Searchable activity timeline for a version's snapshotted key changes.
 * Layout matches Figma Task Management 3001:71735 (Activity Log).
 */
export default function ActivityLogTimeline({ entries = [] }) {
  const [query, setQuery] = useState('');

  const filtered = useMemo(
    () => filterActivityLogEntries(entries, query, formatDateTime),
    [entries, query]
  );

  const hasEntries = Array.isArray(entries) && entries.length > 0;
  const isFiltering = String(query || '').trim().length > 0;

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--dimension-spacing-16)',
        width: '100%',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 'var(--dimension-spacing-16)',
          width: '100%',
        }}
      >
        <Text variant="base-semibold" color="primary" as="h3" style={{ margin: 0 }}>
          Activity Log
        </Text>
        <div style={{ width: 242, flexShrink: 0 }}>
          <Search
            size="medium"
            placeholder="Search activity logs"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onClear={() => setQuery('')}
          />
        </div>
      </div>

      {!hasEntries ? (
        <Text variant="base-regular" color="secondary">
          No activity logged for this version.
        </Text>
      ) : filtered.length === 0 ? (
        <Text variant="base-regular" color="secondary">
          {isFiltering
            ? 'No matching activity.'
            : 'No activity logged for this version.'}
        </Text>
      ) : (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--dimension-spacing-24)',
            width: '100%',
            maxWidth: 800,
          }}
        >
          {filtered.map((entry, idx) => {
            const key = `${idx}-${entry?.display_name || ''}-${entry?.created_datetime || ''}`;
            return (
              <TimelineItem
                key={key}
                entry={entry}
                isLast={idx === filtered.length - 1}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}
